/**
 * Live Slack threads strip — surfaces the 3 most-recent messages from
 * the configured community channel so the Members Directory feels
 * alive from inside the app.
 *
 * Backend endpoint: GET /api/community/slack/feed?limit=3
 *   → { available: bool, channel?, threads: [{author, avatar, text,
 *       ts, reactions, replies, permalink}] }
 *
 * Two visual states:
 *   • Live threads → 3 cards in a row (2 on tablet, 1 on mobile), each
 *     a permalink to the Slack message. Author + text + reaction/reply
 *     counts + relative timestamp.
 *   • Not configured / channel not found → "warming up" placeholder
 *     that still points at the invite URL, so the strip never leaves
 *     the page feeling empty.
 */
import React, { useEffect, useState } from "react";
import { MessageSquare, Heart, ArrowUpRight, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { SlackMark } from "./CommunityButton";
import { SLACK_INVITE_URL, useSlackJoined, slackDestination } from "../lib/slack";

function relTime(ts) {
  if (!ts) return "";
  const sec = Math.floor(Date.now() / 1000 - parseFloat(ts));
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  const d = Math.floor(sec / 86400);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

function initials(name) {
  if (!name) return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

function ThreadCard({ thread, channel }) {
  const [joined] = useSlackJoined();
  const href = thread.permalink || slackDestination(joined);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="slack-thread-card"
      className="group flex flex-col gap-3 p-4 rounded-xl border border-shell-border bg-white hover:border-brand-pink/60 hover:shadow-md transition"
    >
      <div className="flex items-center gap-2">
        {thread.avatar ? (
          <img src={thread.avatar} alt="" className="w-8 h-8 rounded-full" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-brand-pink/10 text-brand-pink text-xs font-bold flex items-center justify-center">
            {initials(thread.author)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">{thread.author}</div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            #{channel || "slack"} · {relTime(thread.ts)}
          </div>
        </div>
        <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-brand-pink transition" />
      </div>
      <p className="text-sm text-foreground line-clamp-4">{thread.text}</p>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Heart className="w-3.5 h-3.5" /> {thread.reactions || 0}
        </span>
        <span className="inline-flex items-center gap-1">
          <MessageSquare className="w-3.5 h-3.5" /> {thread.replies || 0}
        </span>
      </div>
    </a>
  );
}

function PlaceholderStrip() {
  if (!SLACK_INVITE_URL) return null;
  return (
    <a
      href={SLACK_INVITE_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="slack-threads-placeholder"
      className="mt-6 flex items-center gap-3 md:gap-4 p-4 rounded-2xl border border-dashed border-shell-border bg-white/60 hover:border-brand-pink/60 hover:bg-white transition"
    >
      <div className="w-10 h-10 rounded-lg bg-white border border-shell-border flex items-center justify-center shadow-sm">
        <SlackMark className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-pink flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Live from Slack
        </div>
        <div className="mt-0.5 text-sm text-foreground">
          The community is warming up — <span className="font-semibold">join the workspace</span> to be part of the first threads.
        </div>
      </div>
      <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
    </a>
  );
}

export default function SlackThreadsStrip() {
  const [state, setState] = useState({ loading: true, available: false, threads: [], channel: "" });

  useEffect(() => {
    let alive = true;
    api.get("/community/slack/feed", { params: { limit: 3 } })
      .then((r) => { if (alive) setState({ loading: false, ...r.data }); })
      .catch(() => { if (alive) setState({ loading: false, available: false, threads: [] }); });
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    // Minimal skeleton — 3 empty cards so the layout doesn't jump when
    // the fetch resolves.
    return (
      <div className="mt-6 grid gap-3 md:grid-cols-3" aria-hidden data-testid="slack-threads-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-32 rounded-xl border border-shell-border bg-white/60 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!state.available || !state.threads?.length) {
    return <PlaceholderStrip />;
  }

  return (
    <div className="mt-6" data-testid="slack-threads-strip">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-pink inline-flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Live from Slack · #{state.channel}
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {state.threads.map((t) => (
          <ThreadCard key={t.ts} thread={t} channel={state.channel} />
        ))}
      </div>
    </div>
  );
}
